import { Dispatch, SetStateAction, useEffect, useRef, useState } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  Button,
  Collapse,
  Alert,
  Input,
} from "reactstrap";
import classNames from "classnames";

import ComplexOption from "./ComplexOption/ComplexOption";
import StrUtils from "../../../utils/StrUtils";
import { CFieldType } from "../../../types/RecordInterfaces";
import SelectableInput from "../../SelectableInput/SelectableInput";
import Phone from "../../Inputs/Phone";
import PhoneOption from "./ComplexOption/PhoneOption";
import { Field as FieldType } from "../../../types/TenantInterfaces";

export type FieldOptionsType = {
  label: string;
  value: string;
  color: string;
}[];

interface Props {
  recordID: string;
  field: FieldType;
  existingValues: CFieldType[];
  closeRecord: () => void;
  onUpdate: (newValue: any) => void;
  setIsEditing: (val: boolean) => void;
  values: CFieldType[];
  setValues: Dispatch<SetStateAction<CFieldType[]>>;
  type: "Phone" | "Email" | "URL";
}

const Field = { Name: "Type" };

/**
 * Popover/Editor for Complex Field types - Emails, URL, Phone numbers
 * Supports multiple values - Click to edit
 */
export function ComplexField({
  recordID,
  field,
  existingValues,
  closeRecord,
  onUpdate,
  setIsEditing,
  values,
  setValues,
}: Props) {
  const noValues = !existingValues || existingValues?.length < 1;
  const fieldType = field?.FieldType as "Phone" | "Email" | "URL";
  const [editorOpen, setEditorOpen] = useState<boolean>(noValues);
  const [type, setType] = useState<{
    label: string;
    value: string;
    color: string;
  } | null>(null);
  const [selectedUpdate, setSelectUpdate] = useState<CFieldType>();
  const [input, setInput] = useState<string>("");
  const [ext, setExtention] = useState<string | undefined>();
  const inputRef = useRef<HTMLInputElement>(null);
  const [phoneKey, setPhoneKey] = useState(recordID);

  const FieldOptions = StrUtils.getComplexFieldOptions(
    fieldType
  ) as FieldOptionsType;

  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!editorOpen || !inputRef.current) return;

    // Timeout forces this to be called last - Fixes bug with Collapse
    setTimeout(() => inputRef.current?.focus?.(), 0);
  }, [editorOpen]);

  // Supported Complex Fields
  if (!["Phone", "Email", "URL"].includes(fieldType)) return null;

  return (
    <Card className="m-0" style={{ width: 350 }}>
      <CardHeader className="d-flex border-bottom align-items-center justify-content-between px-2 py-1 m-0 ">
        <h5 className="m-0">{fieldType}</h5>
        <Button size="sm" outline onClick={save}>
          Done
        </Button>
      </CardHeader>
      <CardBody className="m-0 p-2">
        {fieldType === "Phone" && editorOpen ? (
          <>
            <Phone
              key={phoneKey}
              autoFocus
              disabled={false}
              on
              onChange={(val: any) => setInput(val)}
              existingValue={input}
              /* @ts-ignore */
              inputSize="sm pb-2"
              setError={setError}
            />
            <h5>Ext.</h5>
            <Input
              placeholder="123"
              value={ext}
              onChange={(e) => setExtention(e.target.value)}
              className="my-2"
              bsSize="sm"
            />
            <SelectableInput
              // @ts-ignore
              field={Field}
              value={type}
              // @ts-ignore
              options={FieldOptions}
              onSelect={setType}
              onDeselect={() => setType(null)}
              onClear={() => setType(null)}
              onOpen={() => setIsEditing(true)}
              onClose={() => setIsEditing(false)}
              modifyTrigger={"click"}
              searchable={false}
            />
          </>
        ) : (
          <>
            <div>
              {values &&
                values?.map((val: CFieldType) =>
                  fieldType === "Phone" ? (
                    <PhoneOption
                      key={val.id}
                      onChange={setValues}
                      complexVal={val}
                      onOpen={() => setError(null)}
                      setError={setError}
                      FieldOptions={FieldOptions}
                      setIsEditing={setIsEditing}
                      setEditorOpen={setEditorOpen}
                      onEdit={() => onEditPhone(val)}
                    />
                  ) : (
                    <ComplexOption
                      key={val.id}
                      complexVal={val}
                      setError={setError}
                      onChange={setValues}
                      setIsEditing={setIsEditing}
                      setEditorOpen={setEditorOpen}
                      onOpen={() => setError(null)}
                      FieldOptions={FieldOptions}
                      fieldType={fieldType}
                    />
                  )
                )}
            </div>
          </>
        )}

        {fieldType !== "Phone" && (
          <Collapse isOpen={editorOpen}>
            <>
              <input
                className="mb-2 border-dark-subtle form-control-sm form-control"
                placeholder={fieldType}
                onChange={(e) => setInput(e.target.value)}
                value={input}
                onKeyDown={(event) =>
                  event.key === "Enter" &&
                  handleSave(event.currentTarget.value, type)
                }
                ref={inputRef}
              />
              <SelectableInput
                // @ts-ignore
                field={Field}
                value={type}
                // @ts-ignore
                options={FieldOptions}
                onSelect={setType}
                onDeselect={() => setType(null)}
                onClear={() => setType(null)}
                onOpen={() => setIsEditing(true)}
                onClose={() => setIsEditing(false)}
                modifyTrigger={"click"}
                searchable={false}
              />
            </>
          </Collapse>
        )}

        {error && (
          <Alert color="danger" className="p-2 mt-3">
            {error}
          </Alert>
        )}

        <div
          className={classNames("d-flex", {
            "justify-content-end": editorOpen,
            "justify-content-between": !editorOpen,
          })}
          style={{ width: "100%" }}
        >
          <Button
            size="sm"
            outline
            onClick={editorOpen ? onCancelEdit : onCancel}
            className="mt-2"
          >
            Cancel
          </Button>

          <Button
            size="sm"
            color="primary"
            onClick={() => {
              setError(null);
              if (editorOpen) {
                if (selectedUpdate) return handleUpdatePhone(input);
                return handleSave(input, type, ext);
              }
              setEditorOpen(true);
            }}
            className="mt-2"
          >
            {!editorOpen ? (
              <>
                Add {fieldType} <i className="mdi mdi-plus-thick"></i>
              </>
            ) : (
              <>Save</>
            )}
          </Button>
        </div>
      </CardBody>
    </Card>
  );

  function onEditPhone(val: CFieldType) {
    setInput(val.Phone || "");
    setExtention(val.Extension);
    setEditorOpen(true);
    setSelectUpdate(val);
  }

  function onCancel() {
    setValues(existingValues);
    closeRecord();
  }

  function save() {
    if (JSON.stringify(values) === JSON.stringify(existingValues)) {
      return closeRecord();
    }

    onUpdate({
      fieldAPIName: field?.APIName,
      recordID: recordID,
      payload: values,
      subitemParent: field?.SubitemParent,
    });

    closeRecord();
  }

  function onCancelEdit() {
    if (existingValues.length === 0) return closeRecord();
    setInput("");
    setExtention("");
    setEditorOpen(false);
    setError(null);
    setSelectUpdate(undefined);
  }

  function handleUpdatePhone(value: string) {
    const existingValue = values.find((v) => v?.Phone === value);

    if (existingValue) return setError(`${fieldType} was already added!`);

    const newVal = { ...selectedUpdate };
    newVal.Phone = value;
    newVal.Extension = ext;

    setValues((prev: CFieldType[]) => {
      const found = prev.findIndex((v) => v.id === newVal.id);
      const newValues = [...(prev || [])];

      newValues[found] = newVal;
      return newValues;
    });

    setEditorOpen(false);
    setInput("");
    setExtention("");
    setType(null);
  }

  function handleSave(value: string, type: any, ext?: string) {
    const existingValue = values.find((v) => v?.[fieldType] === value);

    // Ensure user didn't create a duplicate
    if (existingValue) return setError(`${fieldType} was already added!`);

    // Ensure value passes validation
    if (!StrUtils.validateTextByType(value, fieldType)) {
      return setError(`Invalid ${fieldType}`);
    }

    const newValue: any = {
      id: StrUtils.uuidv4(),
      [fieldType]: value,
      Extension: ext,
      Primary: false,
      Type: {
        ...type,
      },
    };

    setError(null);
    setInput("");
    setExtention("");
    setType(null);
    setEditorOpen(false);
    setPhoneKey(StrUtils.uuidv4());
    setValues((prev: CFieldType[]) => {
      if (!Array.isArray(prev)) return [newValue];

      return [...prev, newValue];
    });
  }
}
